import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Banknote, CheckCircle, Info } from 'lucide-react';
import { profileAPI } from '../../api/profile';
import { Card } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';

export default function BankDetailsPage() {
  const [loading, setLoading]     = useState(true);
  const [isUpdate, setIsUpdate]   = useState(false); // true  → PUT, false → POST
  const [success, setSuccess]     = useState(false);
  const [apiError, setApiError]   = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name:           '',
      account_number: '',
      ifsc:           '',
      bank_name:      '',
      branch:         '',
    },
  });

  /* API #6 — pre-fill if bank account exists ──────────── */
  useEffect(() => {
    profileAPI
      .getProfile()
      .then((res) => {
        const bank = res.data.data?.bank_account;
        if (bank) {
          setIsUpdate(true);
          reset({
            name:           bank.name           ?? '',
            account_number: bank.account_number ?? '',
            ifsc:           bank.ifsc           ?? '',
            bank_name:      bank.bank_name      ?? '',
            branch:         bank.branch         ?? '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reset]);

  /* API #10 (POST) or #11 (PUT) ────────────────────────── */
  const onSubmit = async (data) => {
    setApiError('');
    try {
      if (isUpdate) {
        await profileAPI.updateBankAccount(data);   // PUT /profile/bank-account
      } else {
        await profileAPI.storeBankAccount(data);    // POST /profile/bank-account
        setIsUpdate(true);                          // now it exists
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        // Show first validation error from server
        const first = Object.values(errors)[0]?.[0];
        setApiError(first || 'Validation failed.');
      } else {
        setApiError(err.response?.data?.message || 'Failed to save. Please try again.');
      }
    }
  };

  return (
    <div>
      <PageHeader
        title="Bank Details"
        subtitle={isUpdate ? 'Update your bank account' : 'Add your bank account'}
        showBack
      />

      <div className="px-4 py-4 pb-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col gap-4"
          >
            {/* Mode indicator */}
            <div className="flex items-center gap-2 px-1">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
              >
                <Banknote size={15} color="white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {isUpdate ? 'Update Bank Account' : 'Add Bank Account'}
                </p>
                <p className="text-xs text-gray-400">
                  {isUpdate
                    ? 'Your saved details are pre-filled below'
                    : 'No bank account added yet'}
                </p>
              </div>
            </div>

            <Card>
              {/* Success banner */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"
                >
                  <CheckCircle size={16} />
                  Bank details {isUpdate ? 'updated' : 'saved'} successfully!
                </motion.div>
              )}

              {/* Error banner */}
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600"
                >
                  {apiError}
                </motion.div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                {/* Account Holder Name */}
                <Input
                  label="Account Holder Name"
                  placeholder="As per bank records"
                  error={errors.name?.message}
                  {...register('name', {
                    required: 'Account holder name is required',
                    maxLength: { value: 100, message: 'Max 100 characters' },
                  })}
                />

                {/* Account Number */}
                <Input
                  label="Account Number"
                  placeholder="9 – 20 digit account number"
                  error={errors.account_number?.message}
                  {...register('account_number', {
                    required: 'Account number is required',
                    minLength: { value: 9,  message: 'Minimum 9 digits' },
                    maxLength: { value: 20, message: 'Maximum 20 digits' },
                    pattern:   { value: /^\d+$/, message: 'Only digits allowed' },
                  })}
                />

                {/* IFSC Code */}
                <Input
                  label="IFSC Code"
                  placeholder="e.g. SBIN0001234"
                  error={errors.ifsc?.message}
                  {...register('ifsc', {
                    required: 'IFSC code is required',
                    pattern: {
                      value:   /^[A-Z]{4}0[A-Z0-9]{6}$/,
                      message: 'Invalid IFSC — format: ABCD0123456',
                    },
                    setValueAs: (v) => v.toUpperCase(),
                  })}
                />

                {/* Bank Name */}
                <Input
                  label="Bank Name"
                  placeholder="e.g. State Bank of India"
                  error={errors.bank_name?.message}
                  {...register('bank_name', {
                    required: 'Bank name is required',
                    maxLength: { value: 100, message: 'Max 100 characters' },
                  })}
                />

                {/* Branch */}
                <Input
                  label="Branch"
                  placeholder="Branch name"
                  error={errors.branch?.message}
                  {...register('branch', {
                    required: 'Branch name is required',
                    maxLength: { value: 100, message: 'Max 100 characters' },
                  })}
                />

                <Button type="submit" loading={isSubmitting} fullWidth>
                  {isUpdate ? 'Update Bank Details' : 'Save Bank Details'}
                </Button>
              </form>
            </Card>

            {/* IFSC info note */}
            <div className="flex items-start gap-2 px-1">
              <Info size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-400 leading-relaxed">
                IFSC format: 4 letters + 0 + 6 alphanumeric characters (e.g. SBIN0001234).
                Ensure your bank details are correct for seamless withdrawals.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
